<!--
/**
 * @component HeaderSearch
 * @description Barre de recherche globale (bâtiments, sources, dossiers) intégrée dans le header
 * @example
 * <HeaderSearch />
 */
-->
<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject, onMounted, watch, nextTick } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useDebounceFn } from "@vueuse/core"
import { onClickOutside } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import type {
    SearchResponse,
    SearchBuildingResult,
} from "@core/services/search.service"
import { METER_TYPES_LIST, WEATHER_STATION_TYPES_LIST } from "@core/constants"

const MINIMUM_CHARACTERS = 4
const LIMIT_PREVIEW_RESULT = 5

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            search: {
                placeholder: "Rechercher des bâtiments, des sources...",
                placeholderWithFolders:
                    "Rechercher des bâtiments, des sources, des dossiers...",
                folders: "Dossiers",
                buildings: "Bâtiments",
                sources: "Capteurs & Compteurs",
                seeMore: "Voir les {count} résultats",
                noResult: "Aucun résultat trouvé.",
                minChars:
                    "Aucun résultat trouvé, essayer avec au minimum 4 caractères.",
                meter: "Compteur",
                weatherStation: "Station météo",
                sensor: "Capteur",
                infoTooltip:
                    "La recherche est faite sur les bâtiments, les sources et les dossiers. Rechercher avec au minimum 4 caractères pour voir les résultats.",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")!
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// ================================
// ÉTAT
// ================================

/**
 * Texte de recherche saisi par l'utilisateur
 */
const searchQuery = ref("")

/**
 * Indique si une recherche est en cours
 */
const searching = ref(false)

/**
 * Résultats de la recherche (bâtiments, sources, dossiers)
 */
const results = ref<SearchResponse | null>(null)

/**
 * Référence du conteneur pour détecter les clics extérieurs
 */
const targetRef = ref<HTMLElement | null>(null)

/**
 * Indique si le dropdown des résultats est ouvert
 */
const isOpen = ref(false)

/**
 * Sur mobile : indique si l’overlay de recherche (style Spotlight) est ouverte
 */
const isMobileSearchOpen = ref(false)

/**
 * Référence du conteneur de l’overlay mobile (pour focus de l’input à l’ouverture)
 */
const mobileSearchContainerRef = ref<HTMLElement | null>(null)

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Placeholder de l'input selon le type d'utilisateur (avec ou sans dossiers)
 */
const getPlaceholder = computed(() =>
    authStore.isClientHomeys
        ? t("search.placeholderWithFolders")
        : t("search.placeholder")
)

/**
 * Indique s'il n'y a aucun résultat
 */
const getHasNoResult = computed(() => {
    if (!results.value) return false
    const { buildings, sources, folders } = results.value
    const hasFolders = authStore.isClientHomeys && folders.data.length > 0
    return (
        buildings.data.length === 0 && sources.data.length === 0 && !hasFolders
    )
})

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Formate l'identifiant d'une source pour l'affichage (PDL/PCE avec espaces)
 */
function formatSourceId(idsource: string, idtype: string): string {
    if (["pdl", "pce"].includes(idtype) && idsource?.length === 14) {
        return idsource.replace(/(.{2})/g, "$1 ").trim()
    }
    return idsource ?? ""
}

/**
 * Retourne le libellé du type de source (Compteur, Station météo, Capteur)
 */
function formatSourceTypeLabel(idtype: string): string {
    if (METER_TYPES_LIST.includes(idtype)) return t("search.meter")
    if (WEATHER_STATION_TYPES_LIST.includes(idtype))
        return t("search.weatherStation")
    return t("search.sensor")
}

/**
 * Formate l'adresse d'un bâtiment pour l'affichage
 */
function formatBuildingAddress(
    address: SearchBuildingResult["address"]
): string {
    if (!address) return ""
    if (typeof address === "string") return address
    return [address.street, address.zipcode, address.city]
        .filter(Boolean)
        .join(" ")
}

// ================================
// INTERACTIONS
// ================================

/**
 * Ferme le dropdown lors d'un clic à l'extérieur
 */
onClickOutside(targetRef, () => {
    isOpen.value = false
})

/**
 * Lance la recherche avec debounce (1 seconde)
 */
const debouncedSearch = useDebounceFn(async () => {
    await onLaunchSearch()
}, 1000)

/**
 * Gère la saisie dans l'input avec debounce et minimum de caractères
 */
function onDebounceInput(): void {
    const trimmed = searchQuery.value?.trim() ?? ""
    if (trimmed.length !== 0) searching.value = true

    if (trimmed.length >= MINIMUM_CHARACTERS) {
        debouncedSearch()
    } else {
        results.value = null
        searching.value = false
        isOpen.value = false
    }
}

/**
 * Gère la touche Backspace pour réinitialiser quand l'input est vide
 */
function onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Backspace" && !searchQuery.value) {
        onResetResults()
    }
    if (event.key === "Escape") {
        isOpen.value = false
        isMobileSearchOpen.value = false
    }
}

/**
 * Lance la recherche via l'API
 */
async function onLaunchSearch(): Promise<void> {
    const trimmed = searchQuery.value?.trim() ?? ""
    if (!trimmed) {
        searching.value = false
        return
    }

    try {
        const data = await services.$search(trimmed, LIMIT_PREVIEW_RESULT)
        results.value = data
        isOpen.value = true
    } catch {
        results.value = {
            buildings: { data: [], max: 0 },
            sources: { data: [], max: 0 },
            folders: { data: [], max: 0 },
        }
        isOpen.value = true
    } finally {
        searching.value = false
    }
}

/**
 * Réinitialise les résultats et ferme le dropdown
 */
function onResetResults(): void {
    results.value = null
    searchQuery.value = ""
    searching.value = false
    isOpen.value = false
    isMobileSearchOpen.value = false
}

/**
 * Navigation vers un bâtiment
 */
function onClickBuilding(id: string): void {
    router.push({ path: `/building/${id}` })
    onResetResults()
}

/**
 * Navigation vers une source (réinitialise le bâtiment)
 */
function onClickSource(id: string): void {
    router.push({ path: `/source/${id}` })
    onResetResults()
}

/**
 * Navigation vers un dossier
 */
function onClickFolder(id: string): void {
    router.push({ path: `/folder/${id}` })
    onResetResults()
}

/**
 * Navigation vers la page de recherche avec filtre par type
 */
function onClickShowMore(type: "building" | "source" | "folder"): void {
    const query = { ...route.query, search: searchQuery.value }
    if (type === "building") {
        router.push({
            path: "/building",
            query: { ...query, type: "building" },
        })
    }
    if (type === "source") {
        router.push({
            path: "/source",
            query: { search: searchQuery.value, filters: [] },
        })
    }
    if (type === "folder") {
        router.push({ path: "/building", query: { ...query, type: "folder" } })
    }
    isOpen.value = false
}

// ================================
// LIFECYCLE
// ================================

/**
 * Ouvre l’overlay de recherche mobile et focus l’input au prochain tick
 */
function onOpenMobileSearch(): void {
    isMobileSearchOpen.value = true
    nextTick(() => {
        mobileSearchContainerRef.value
            ?.querySelector<HTMLInputElement>("input")
            ?.focus()
    })
}

/**
 * Ferme l’overlay de recherche mobile
 */
function onCloseMobileSearch(): void {
    isMobileSearchOpen.value = false
}

// Focus de l'input quand l'overlay mobile s'ouvre
watch(isMobileSearchOpen, (open) => {
    if (open) {
        nextTick(() => {
            mobileSearchContainerRef.value
                ?.querySelector<HTMLInputElement>("input")
                ?.focus()
        })
    }
})

onMounted(() => {
    const querySearch = route.query.search
    if (querySearch && typeof querySearch === "string") {
        searchQuery.value = querySearch
        onLaunchSearch()
    }
})
</script>

<template>
    <!-- ================================ -->
    <!-- BARRE DE RECHERCHE (desktop) / ICÔNE + OVERLAY (mobile) -->
    <!-- ================================ -->
    <div
        ref="targetRef"
        class="header-search relative z-[1200] w-full max-w-[30rem] md:min-w-[30rem] max-lg:max-w-[25rem]"
        :data-focus="isOpen"
        :data-results="results && isOpen"
    >
        <!-- Desktop : barre de recherche inline -->
        <div class="hidden md:block">
            <BaseInputGroup class="header-search__input-group">
                <BaseInputGroupAddon
                    class="rounded-b-none"
                    :class="{
                        'rounded-b-none!': !!results && isOpen,
                    }"
                >
                    <i class="pi pi-search text-surface-600" />
                </BaseInputGroupAddon>
                <BaseInputText
                    v-model="searchQuery"
                    :placeholder="getPlaceholder"
                    input-id="header-search-input"
                    class="border-0 shadow-none focus:ring-0"
                    @input="onDebounceInput"
                    @keydown="onKeyDown"
                    @focus="isOpen = true"
                    :class="{
                        'rounded-b-none!': !!results && isOpen,
                    }"
                />
                <BaseInputGroupAddon>
                    <i
                        v-if="searching"
                        class="pi pi-spinner pi-spin text-surface-600"
                    />
                    <i
                        v-else
                        class="pi pi-info-circle text-surface-600"
                        v-tooltip="t('search.infoTooltip')"
                    />
                </BaseInputGroupAddon>
            </BaseInputGroup>

            <!-- ================================ -->
            <!-- DROPDOWN RÉSULTATS -->
            <!-- ================================ -->
            <div
                v-if="results && isOpen"
                class="header-search__results absolute left-0 right-0 top-full z-[1200] mt-0 max-h-[24rem] overflow-y-auto rounded-b-lg border border-t-0 border-surface-200 bg-white p-4 shadow-lg"
            >
                <!-- Dossiers -->
                <div
                    v-if="
                        results.folders.data.length > 0 &&
                        authStore.isClientHomeys
                    "
                    class="header-search__section"
                >
                    <h4 class="mb-2 text-sm font-light text-surface-700">
                        {{ t("search.folders") }}
                    </h4>
                    <div
                        v-for="folder in results.folders.data"
                        :key="`search-folder-${folder._id}`"
                        v-ripple
                        class="header-search__item flex cursor-pointer items-center gap-2 py-2 text-primary hover:font-semibold"
                        @mousedown="onClickFolder(folder._id)"
                    >
                        <i class="pi pi-folder text-surface-600" />
                        <span class="truncate">{{ folder.name }}</span>
                    </div>
                </div>

                <!-- Voir plus dossiers -->
                <div
                    v-if="
                        results.folders.max > LIMIT_PREVIEW_RESULT &&
                        authStore.isClientHomeys
                    "
                    v-ripple
                    class="header-search__show-more mt-2 flex cursor-pointer items-center justify-end gap-1 text-sm text-primary hover:font-semibold"
                    @mousedown="onClickShowMore('folder')"
                >
                    <span>{{
                        t("search.seeMore", { count: results.folders.max })
                    }}</span>
                    <i class="pi pi-arrow-right text-xs" />
                </div>

                <!-- Bâtiments -->
                <div
                    v-if="results.buildings.data.length > 0"
                    class="header-search__section mt-3"
                    :class="{
                        'border-t border-surface-200 pt-3':
                            results.folders.data.length > 0,
                    }"
                >
                    <h4 class="mb-2 text-sm font-light text-surface-700">
                        {{ t("search.buildings") }}
                    </h4>
                    <div
                        v-for="building in results.buildings.data"
                        :key="`search-building-${building._id}`"
                        v-ripple
                        class="header-search__item flex cursor-pointer items-center gap-2 py-2 text-primary hover:font-semibold"
                        @mousedown="onClickBuilding(building._id)"
                    >
                        <i class="pi pi-building text-surface-600" />
                        <div class="min-w-0 flex-1 overflow-hidden">
                            <p class="truncate">
                                {{
                                    building.name ||
                                    formatBuildingAddress(building.address)
                                }}
                            </p>
                            <p
                                v-if="building.name && building.address"
                                class="truncate text-xs font-normal text-surface-600"
                            >
                                {{ formatBuildingAddress(building.address) }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Voir plus bâtiments -->
                <div
                    v-if="results.buildings.max > LIMIT_PREVIEW_RESULT"
                    v-ripple
                    class="header-search__show-more mt-2 flex cursor-pointer items-center justify-end gap-1 text-sm text-primary hover:font-semibold"
                    @mousedown="onClickShowMore('building')"
                >
                    <span>{{
                        t("search.seeMore", { count: results.buildings.max })
                    }}</span>
                    <i class="pi pi-arrow-right text-xs" />
                </div>

                <!-- Sources -->
                <div
                    v-if="results.sources.data.length > 0"
                    class="header-search__section mt-3"
                    :class="{
                        'border-t border-surface-200 pt-3':
                            results.buildings.data.length > 0,
                    }"
                >
                    <h4 class="mb-2 text-sm font-light text-surface-700">
                        {{ t("search.sources") }}
                    </h4>
                    <div
                        v-for="source in results.sources.data"
                        :key="`search-source-${source._id_source}`"
                        v-ripple
                        class="header-search__item flex cursor-pointer items-center gap-2 py-2 text-primary hover:font-semibold"
                        @mousedown="onClickSource(source._id_source)"
                    >
                        <BaseSourceIcon
                            :type="source.idtype"
                            class="h-5 w-5 shrink-0"
                        />
                        <div class="min-w-0 flex-1 overflow-hidden">
                            <p class="truncate text-xs text-surface-600">
                                {{ formatSourceTypeLabel(source.idtype) }}
                            </p>
                            <p class="truncate">
                                <span v-if="source.name"
                                    >{{ source.name }} -
                                </span>
                                <span>{{
                                    formatSourceId(
                                        source.idsource,
                                        source.idtype
                                    )
                                }}</span>
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Voir plus sources -->
                <div
                    v-if="results.sources.max > LIMIT_PREVIEW_RESULT"
                    v-ripple
                    class="header-search__show-more mt-2 flex cursor-pointer items-center justify-end gap-1 text-sm text-primary hover:font-semibold"
                    @mousedown="onClickShowMore('source')"
                >
                    <span>{{
                        t("search.seeMore", { count: results.sources.max })
                    }}</span>
                    <i class="pi pi-arrow-right text-xs" />
                </div>

                <!-- Aucun résultat -->
                <div
                    v-if="getHasNoResult"
                    class="header-search__no-result mt-3 text-sm text-surface-600"
                >
                    <p>
                        {{
                            (searchQuery?.trim()?.length ?? 0) <
                            MINIMUM_CHARACTERS
                                ? t("search.minChars")
                                : t("search.noResult")
                        }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Mobile : icône recherche → ouvre overlay type Spotlight -->
        <div class="flex items-center md:hidden">
            <BaseButton
                type="button"
                icon="pi pi-search"
                severity="secondary"
                text
                rounded
                class="text-white! hover:text-surface-700!"
                aria-label="Rechercher"
                @click="onOpenMobileSearch"
            />
        </div>

        <!-- Overlay recherche mobile (style Spotlight) -->
        <Teleport to="body">
            <Transition name="fade">
                <div
                    v-if="isMobileSearchOpen"
                    class="fixed inset-0 z-2000 flex flex-col justify-center items-center"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Recherche"
                >
                    <button
                        type="button"
                        class="absolute inset-0 bg-black/40"
                        aria-label="Fermer"
                        @click="onCloseMobileSearch"
                    />
                    <div
                        ref="mobileSearchContainerRef"
                        class="relative z-10 flex max-h-[85vh] w-full max-w-[98vw] flex-col overflow-hidden rounded-xl border border-surface-200 bg-white shadow-xl"
                        @click.stop
                    >
                        <div
                            class="flex shrink-0 items-center gap-2 border-b border-surface-200 p-3"
                        >
                            <BaseInputGroup
                                class="header-search__input-group flex-1"
                            >
                                <BaseInputGroupAddon
                                    class="rounded-r-none"
                                    :class="{
                                        'rounded-b-none!': !!results && isOpen,
                                    }"
                                >
                                    <i class="pi pi-search text-surface-600" />
                                </BaseInputGroupAddon>
                                <BaseInputText
                                    v-model="searchQuery"
                                    :placeholder="getPlaceholder"
                                    input-id="header-search-input-mobile"
                                    class="border-0 shadow-none focus:ring-0"
                                    @input="onDebounceInput"
                                    @keydown="onKeyDown"
                                    @focus="isOpen = true"
                                    :class="{
                                        'rounded-b-none!': !!results && isOpen,
                                    }"
                                />
                                <BaseInputGroupAddon>
                                    <i
                                        v-if="searching"
                                        class="pi pi-spinner pi-spin text-surface-600"
                                    />
                                    <i
                                        v-else
                                        class="pi pi-info-circle text-surface-600"
                                        v-tooltip="t('search.infoTooltip')"
                                    />
                                </BaseInputGroupAddon>
                            </BaseInputGroup>
                            <BaseButton
                                type="button"
                                icon="pi pi-times"
                                severity="secondary"
                                text
                                rounded
                                aria-label="Fermer"
                                @click="onCloseMobileSearch"
                            />
                        </div>
                        <div
                            v-if="results && isOpen"
                            class="header-search__results overflow-y-auto p-2"
                        >
                            <!-- Dossiers -->
                            <div
                                v-if="
                                    results.folders.data.length > 0 &&
                                    authStore.isClientHomeys
                                "
                                class="header-search__section"
                            >
                                <h4
                                    class="mb-2 text-sm font-light text-surface-700"
                                >
                                    {{ t("search.folders") }}
                                </h4>
                                <div
                                    v-for="folder in results.folders.data"
                                    :key="`search-folder-mobile-${folder._id}`"
                                    v-ripple
                                    class="header-search__item flex cursor-pointer items-center gap-2 py-2 text-primary hover:font-semibold"
                                    @mousedown="onClickFolder(folder._id)"
                                >
                                    <i class="pi pi-folder text-surface-600" />
                                    <span class="truncate">{{
                                        folder.name
                                    }}</span>
                                </div>
                            </div>
                            <div
                                v-if="
                                    results.folders.max >
                                        LIMIT_PREVIEW_RESULT &&
                                    authStore.isClientHomeys
                                "
                                v-ripple
                                class="header-search__show-more mt-2 flex cursor-pointer items-center justify-end gap-1 text-sm text-primary hover:font-semibold"
                                @mousedown="onClickShowMore('folder')"
                            >
                                <span>{{
                                    t("search.seeMore", {
                                        count: results.folders.max,
                                    })
                                }}</span>
                                <i class="pi pi-arrow-right text-xs" />
                            </div>
                            <!-- Bâtiments -->
                            <div
                                v-if="results.buildings.data.length > 0"
                                class="header-search__section mt-3"
                                :class="{
                                    'border-t border-surface-200 pt-3':
                                        results.folders.data.length > 0,
                                }"
                            >
                                <h4
                                    class="mb-2 text-sm font-light text-surface-700"
                                >
                                    {{ t("search.buildings") }}
                                </h4>
                                <div
                                    v-for="building in results.buildings.data"
                                    :key="`search-building-mobile-${building._id}`"
                                    v-ripple
                                    class="header-search__item flex cursor-pointer items-center gap-2 py-2 text-primary hover:font-semibold"
                                    @mousedown="onClickBuilding(building._id)"
                                >
                                    <i
                                        class="pi pi-building text-surface-600"
                                    />
                                    <div class="min-w-0 flex-1 overflow-hidden">
                                        <p class="truncate">
                                            {{
                                                building.name ||
                                                formatBuildingAddress(
                                                    building.address
                                                )
                                            }}
                                        </p>
                                        <p
                                            v-if="
                                                building.name &&
                                                building.address
                                            "
                                            class="truncate text-xs font-normal text-surface-600"
                                        >
                                            {{
                                                formatBuildingAddress(
                                                    building.address
                                                )
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="
                                    results.buildings.max > LIMIT_PREVIEW_RESULT
                                "
                                v-ripple
                                class="header-search__show-more mt-2 flex cursor-pointer items-center justify-end gap-1 text-sm text-primary hover:font-semibold"
                                @mousedown="onClickShowMore('building')"
                            >
                                <span>{{
                                    t("search.seeMore", {
                                        count: results.buildings.max,
                                    })
                                }}</span>
                                <i class="pi pi-arrow-right text-xs" />
                            </div>
                            <!-- Sources -->
                            <div
                                v-if="results.sources.data.length > 0"
                                class="header-search__section mt-3"
                                :class="{
                                    'border-t border-surface-200 pt-3':
                                        results.buildings.data.length > 0,
                                }"
                            >
                                <h4
                                    class="mb-2 text-sm font-light text-surface-700"
                                >
                                    {{ t("search.sources") }}
                                </h4>
                                <div
                                    v-for="source in results.sources.data"
                                    :key="`search-source-mobile-${source._id_source}`"
                                    v-ripple
                                    class="header-search__item flex cursor-pointer items-center gap-2 py-2 text-primary hover:font-semibold"
                                    @mousedown="
                                        onClickSource(source._id_source)
                                    "
                                >
                                    <BaseSourceIcon
                                        :type="source.idtype"
                                        class="h-5 w-5 shrink-0"
                                    />
                                    <div class="min-w-0 flex-1 overflow-hidden">
                                        <p
                                            class="truncate text-xs text-surface-600"
                                        >
                                            {{
                                                formatSourceTypeLabel(
                                                    source.idtype
                                                )
                                            }}
                                        </p>
                                        <p class="truncate">
                                            <span v-if="source.name"
                                                >{{ source.name }} -
                                            </span>
                                            <span>{{
                                                formatSourceId(
                                                    source.idsource,
                                                    source.idtype
                                                )
                                            }}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="
                                    results.sources.max > LIMIT_PREVIEW_RESULT
                                "
                                v-ripple
                                class="header-search__show-more mt-2 flex cursor-pointer items-center justify-end gap-1 text-sm text-primary hover:font-semibold"
                                @mousedown="onClickShowMore('source')"
                            >
                                <span>{{
                                    t("search.seeMore", {
                                        count: results.sources.max,
                                    })
                                }}</span>
                                <i class="pi pi-arrow-right text-xs" />
                            </div>
                            <div
                                v-if="getHasNoResult"
                                class="header-search__no-result mt-3 text-sm text-surface-600"
                            >
                                <p>
                                    {{
                                        (searchQuery?.trim()?.length ?? 0) <
                                        MINIMUM_CHARACTERS
                                            ? t("search.minChars")
                                            : t("search.noResult")
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
